import { useEffect, useState, useRef } from "react";
import { Upload, RefreshCw, Boxes, AlertCircle, CheckCircle2, FileSpreadsheet, Download, GitCompare, ChevronRight } from "lucide-react";
import { PageHeader } from "../components/Layout";
import { api, formatApiError, API_BASE } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { toast } from "sonner";
import { downloadCsv, downloadXlsx } from "../lib/exporters";

function TankCategoryPill({ value }) {
  if (!value || value === "—") {
    return <span className="text-gray-300 font-mono text-xs">—</span>;
  }
  const tone = value === "FOOD"
    ? "bg-[#99D420]/15 text-[#3a6500] border-[#99D420]/50"
    : value === "CHEM"
    ? "bg-[#F1471D]/15 text-[#9b2a0f] border-[#F1471D]/50"
    : "bg-gray-100 text-gray-700 border-gray-300";
  const label = value === "FOOD" ? "FOOD" : value === "CHEM" ? "CHEM" : value;
  return <span className={`inline-block text-[10px] font-bold uppercase tracking-[0.12em] px-2 py-0.5 border rounded-sm ${tone}`}>{label}</span>;
}

function ConditionPill({ value }) {
  const map = {
    SOUND: "ps-pill ps-pill-success",
    DAMAGED: "ps-pill ps-pill-error",
    UNDER_SURVEY: "ps-pill ps-pill-warning",
  };
  const cls = map[value] || "ps-pill ps-pill-neutral";
  return (
    <span className={cls}>
      <span className="dot" />
      {value === "UNDER_SURVEY" ? "Under Survey" : (value === "—" ? "—" : value.charAt(0) + value.slice(1).toLowerCase())}
    </span>
  );
}

export default function Stock() {
  const { user } = useAuth();
  const canImport = user?.role === "admin" || user?.role === "supervisor";
  const canReconcile = ["admin", "supervisor", "general_manager", "depot_manager"].includes(user?.role);
  const [tab, setTab] = useState("overview"); // overview | reconcile
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [importing, setImporting] = useState(false);
  const [lastImport, setLastImport] = useState(null);
  const [carrierFilter, setCarrierFilter] = useState("");
  const fileRef = useRef(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/stock/summary");
      setData(data);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const onFile = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setImporting(true);
    setLastImport(null);
    try {
      const fd = new FormData();
      fd.append("file", f);
      const res = await fetch(`${API_BASE}/stock/import`, {
        method: "POST",
        credentials: "include",
        body: fd,
      });
      const json = await res.json();
      if (!res.ok) throw new Error(json.detail || "Import failed");
      setLastImport(json);
      toast.success(`Imported ${json.inserted} rows from ${json.filename}`);
      await load();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setImporting(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  return (
    <div data-testid="stock-page">
      <PageHeader
        title="Empty Stock"
        subtitle="Containers currently inside the depot — grouped by ISO type, grade and condition."
        testid="stock-header"
        actions={
          <>
            <button className="ps-btn ps-btn-secondary" onClick={load} data-testid="refresh-stock">
              <RefreshCw className="w-4 h-4" /> Refresh
            </button>
            <button
              className="ps-btn ps-btn-secondary"
              disabled={!data?.breakdown?.length}
              onClick={() => downloadCsv(data.breakdown, `stock_breakdown_${new Date().toISOString().slice(0,10)}`, [
                { key: "carrier", label: "Carrier" },
                { key: "iso_type", label: "ISO Type" },
                { key: "tank_category", label: "Tank Grade" },
                { key: "grade", label: "Grade" },
                { key: "condition", label: "Condition" },
                { key: "count", label: "Count" },
                { key: "sample", label: "Sample Units", format: (v) => Array.isArray(v) ? v.join(" | ") : "" },
              ])}
              data-testid="stock-export-csv"
            >
              <Download className="w-4 h-4" /> CSV
            </button>
            <button
              className="ps-btn ps-btn-secondary"
              disabled={!data?.breakdown?.length}
              onClick={() => downloadXlsx(data.breakdown, `stock_breakdown_${new Date().toISOString().slice(0,10)}`, [
                { key: "carrier", label: "Carrier" },
                { key: "iso_type", label: "ISO Type" },
                { key: "tank_category", label: "Tank Grade" },
                { key: "grade", label: "Grade" },
                { key: "condition", label: "Condition" },
                { key: "count", label: "Count" },
                { key: "sample", label: "Sample Units", format: (v) => Array.isArray(v) ? v.join(" | ") : "" },
              ], "Stock")}
              data-testid="stock-export-xlsx"
            >
              <FileSpreadsheet className="w-4 h-4" /> XLSX
            </button>
            {canImport && (
              <>
                <input
                  ref={fileRef}
                  type="file"
                  accept=".csv,.txt,.xlsx,.xlsm"
                  className="hidden"
                  onChange={onFile}
                  data-testid="stock-import-file"
                />
                <button
                  className="ps-btn ps-btn-primary"
                  onClick={() => fileRef.current?.click()}
                  disabled={importing}
                  data-testid="stock-import-button"
                >
                  <Upload className="w-4 h-4" /> {importing ? "Importing…" : "Import CSV / XLSX"}
                </button>
              </>
            )}
          </>
        }
      />

      <div className="p-6 sm:p-8 space-y-6">
        {/* Tabs */}
        <div className="flex items-center gap-1 border-b border-gray-200" data-testid="stock-tabs">
          <button
            onClick={() => setTab("overview")}
            className={`px-4 py-2 text-sm font-bold uppercase tracking-[0.14em] border-b-2 -mb-px transition-colors ${
              tab === "overview"
                ? "border-[#006E9E] text-[#006E9E]"
                : "border-transparent text-gray-500 hover:text-gray-900"
            }`}
            data-testid="tab-overview"
          >
            <span className="inline-flex items-center gap-2"><Boxes className="w-4 h-4" /> Overview</span>
          </button>
          {canReconcile && (
            <button
              onClick={() => setTab("reconcile")}
              className={`px-4 py-2 text-sm font-bold uppercase tracking-[0.14em] border-b-2 -mb-px transition-colors ${
                tab === "reconcile"
                  ? "border-[#006E9E] text-[#006E9E]"
                  : "border-transparent text-gray-500 hover:text-gray-900"
              }`}
              data-testid="tab-reconcile"
            >
              <span className="inline-flex items-center gap-2"><GitCompare className="w-4 h-4" /> Reconciliation</span>
            </button>
          )}
        </div>

        {tab === "reconcile" ? (
          <ReconcileTab carriers={data?.by_carrier ?? []} />
        ) : (
        <>
        {/* Total in depot KPI + per-carrier strip */}
        <div className="grid lg:grid-cols-3 gap-4">
          <div className="border border-gray-200 bg-white p-5 flex items-center gap-5" data-testid="stock-total">
            <div className="w-12 h-12 rounded-sm bg-blue-50 text-[#006E9E] flex items-center justify-center">
              <Boxes className="w-6 h-6" />
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-gray-500">Total empty units in depot</div>
              <div className="font-mono font-black text-4xl tabular-nums text-gray-900">{loading ? "—" : (data?.total_in_depot ?? 0)}</div>
            </div>
          </div>

          {/* By carrier rollup */}
          <div className="border border-gray-200 bg-white p-5 lg:col-span-2" data-testid="stock-by-carrier">
            <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-gray-500 mb-3">In-depot by carrier</div>
            {loading && <div className="text-gray-400 text-sm">Loading…</div>}
            {!loading && (data?.by_carrier?.length ?? 0) === 0 && (
              <div className="text-gray-400 text-sm">No data.</div>
            )}
            {!loading && data?.by_carrier?.length > 0 && (
              <div className="flex flex-wrap gap-2">
                <button
                  onClick={() => setCarrierFilter("")}
                  className={`px-3 py-2 border text-sm font-mono transition-colors ${
                    !carrierFilter
                      ? "bg-[#006E9E] text-white border-[#006E9E]"
                      : "border-gray-200 text-gray-700 hover:border-gray-400"
                  }`}
                  data-testid="carrier-pill-all"
                >
                  ALL <span className="font-bold ml-1">{data.total_in_depot}</span>
                </button>
                {data.by_carrier.map((c) => (
                  <button
                    key={c.carrier}
                    onClick={() => setCarrierFilter(c.carrier === carrierFilter ? "" : c.carrier)}
                    className={`px-3 py-2 border text-sm font-mono transition-colors ${
                      carrierFilter === c.carrier
                        ? "bg-[#006E9E] text-white border-[#006E9E]"
                        : "border-gray-200 text-gray-700 hover:border-gray-400"
                    }`}
                    data-testid={`carrier-pill-${c.carrier}`}
                  >
                    {c.carrier} <span className="font-bold ml-1">{c.count}</span>
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Tank category breakdown — only relevant when there are tanks tagged */}
        {!loading && data?.by_tank_category?.some((c) => c.tank_category !== "—") && (
          <div className="border border-gray-200 bg-white p-5" data-testid="stock-by-category">
            <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-gray-500 mb-3">In-depot by tank grade</div>
            <div className="flex flex-wrap gap-2">
              {data.by_tank_category.map((c) => {
                const isFood = c.tank_category === "FOOD";
                const isChem = c.tank_category === "CHEM";
                const tone = isFood
                  ? "bg-[#99D420]/15 text-[#3a6500] border-[#99D420]/60"
                  : isChem
                  ? "bg-[#F1471D]/15 text-[#9b2a0f] border-[#F1471D]/60"
                  : "bg-gray-50 text-gray-600 border-gray-300";
                const label = isFood ? "FOOD GRADE" : isChem ? "CHEMICAL" : "NOT SET";
                return (
                  <div key={c.tank_category} className={`px-4 py-2 border rounded-sm ${tone}`}>
                    <div className="text-[10px] font-bold uppercase tracking-[0.16em] opacity-80">{label}</div>
                    <div className="font-mono font-black text-2xl tabular-nums">{c.count}</div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Last import result */}
        {lastImport && (
          <div className="border border-emerald-200 bg-emerald-50 p-4 flex items-start gap-3" data-testid="last-import-result">
            <CheckCircle2 className="w-5 h-5 text-emerald-700 mt-0.5 shrink-0" />
            <div className="text-sm text-emerald-900">
              <div className="font-semibold">Import complete — {lastImport.filename}</div>
              <div>Inserted: <span className="font-mono">{lastImport.inserted}</span> · Skipped: <span className="font-mono">{lastImport.skipped}</span></div>
              {lastImport.errors?.length > 0 && (
                <details className="mt-2 text-xs">
                  <summary className="cursor-pointer">{lastImport.errors.length} row error(s)</summary>
                  <pre className="font-mono mt-1 bg-white p-2 rounded-sm border border-emerald-100">{JSON.stringify(lastImport.errors, null, 2)}</pre>
                </details>
              )}
            </div>
          </div>
        )}

        {/* CSV format help */}
        {canImport && !lastImport && (
          <div className="border border-gray-200 bg-gray-50 p-4 text-sm text-gray-700" data-testid="csv-help">
            <div className="flex items-start gap-2">
              <AlertCircle className="w-4 h-4 mt-0.5 text-gray-500 shrink-0" />
              <div>
                <div className="font-semibold mb-1">Supported formats: CSV or XLSX</div>
                <div className="text-xs text-gray-600">
                  XLSX is parsed sheet-by-sheet (sheet name &quot;GATE IN&quot; / &quot;GATE OUT&quot; sets the gate type) and headers are auto-detected.
                  Sizes like <code className="font-mono">20STD</code> / <code className="font-mono">40HC</code> map to ISO codes (22G1, 45G1…) automatically.
                  Conditions like <code className="font-mono">HOLED / DAMAGED / DENTED / CUT / GRADE 3</code> normalize to <strong>DAMAGED</strong>.
                  CSV headers (case-insensitive): <code className="font-mono text-[11px] block bg-white border border-gray-200 p-2 mt-1 rounded-sm">container_number, iso_type, grade, condition, last_carrier, gate_type, event_time, location_code, customer, notes</code>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Breakdown table */}
        <div className="border border-gray-200 bg-white">
          <div className="px-5 py-3 border-b border-gray-200 flex items-center justify-between">
            <div className="text-[11px] uppercase tracking-[0.18em] font-bold text-gray-600">
              Breakdown by carrier · ISO type · grade · condition
              {carrierFilter && <span className="ml-2 text-[#006E9E] normal-case tracking-normal">— filtered by {carrierFilter}</span>}
            </div>
            {carrierFilter && (
              <button
                onClick={() => setCarrierFilter("")}
                className="text-xs text-gray-500 hover:text-gray-900 underline"
                data-testid="clear-carrier-filter"
              >Clear filter</button>
            )}
          </div>
          <table className="ps-table ps-table-cards" data-testid="stock-breakdown-table">
            <thead>
              <tr>
                <th>Carrier</th>
                <th>ISO type</th>
                <th>Tank grade</th>
                <th>Grade</th>
                <th>Condition</th>
                <th className="text-right">Count</th>
                <th>Sample units</th>
              </tr>
            </thead>
            <tbody>
              {loading && <tr><td colSpan={7} className="text-center text-gray-400 py-10">Loading…</td></tr>}
              {!loading && (data?.breakdown?.length ?? 0) === 0 && (
                <tr><td colSpan={7} className="text-center text-gray-400 py-10">No containers in depot. Gate some in or import a stock CSV.</td></tr>
              )}
              {!loading && data?.breakdown
                ?.filter((r) => !carrierFilter || r.carrier === carrierFilter)
                .map((row, i) => (
                <tr key={i} data-testid={`stock-row-${i}`}>
                  <td data-label="Carrier" className="font-mono font-bold">{row.carrier}</td>
                  <td data-label="ISO type" className="font-mono font-semibold">{row.iso_type}</td>
                  <td data-label="Tank grade"><TankCategoryPill value={row.tank_category} /></td>
                  <td data-label="Grade"><span className="ps-pill ps-pill-neutral"><span className="dot" />{row.grade}</span></td>
                  <td data-label="Condition"><ConditionPill value={row.condition} /></td>
                  <td data-label="Count" className="text-right font-mono font-bold text-lg tabular-nums">{row.count}</td>
                  <td data-label="Sample units" className="font-mono text-xs text-gray-600 mobile-row-full">{row.sample.join(", ")}{row.count > row.sample.length ? ` … +${row.count - row.sample.length}` : ""}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        </>
        )}
      </div>
    </div>
  );
}

// ────────────────────────── Reconcile tab ──────────────────────────
function ReconcileTab({ carriers }) {
  const [file, setFile] = useState(null);
  const [carrier, setCarrier] = useState("");
  const [running, setRunning] = useState(false);
  const [result, setResult] = useState(null);
  const fileInputRef = useRef(null);

  const carrierOptions = (carriers || [])
    .filter((c) => c.carrier && c.carrier !== "—")
    .map((c) => c.carrier);

  const runPreview = async () => {
    if (!file) { toast.error("Choose a carrier stock file first"); return; }
    if (!carrier) { toast.error("Pick the carrier this report is for"); return; }
    setRunning(true);
    setResult(null);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("carrier_code", carrier);
      const res = await fetch(`${API_BASE}/stock/reconcile/preview`, {
        method: "POST",
        credentials: "include",
        body: fd,
      });
      const json = await res.json();
      if (!res.ok) throw new Error(json.detail || "Reconcile failed");
      setResult(json);
      toast.success(
        `${json.totals.matched} matched · ` +
        `${json.totals.at_carrier_not_at_depot} carrier-only · ` +
        `${json.totals.at_depot_not_at_carrier} depot-only`
      );
    } catch (err) {
      toast.error(err.message || String(err));
    } finally {
      setRunning(false);
    }
  };

  const reset = () => {
    setFile(null);
    setResult(null);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  return (
    <div className="space-y-6" data-testid="reconcile-tab">
      {/* Controls */}
      <div className="border border-gray-200 bg-white p-5 space-y-4" data-testid="reconcile-controls">
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 rounded-sm bg-amber-50 text-amber-700 flex items-center justify-center shrink-0">
            <GitCompare className="w-5 h-5" />
          </div>
          <div className="flex-1">
            <div className="text-base font-bold text-gray-900">Compare a carrier stock report against our depot</div>
            <div className="text-sm text-gray-600 mt-1">
              Upload the carrier&apos;s latest stock list (CSV/XLSX). We&apos;ll diff it against the containers we currently
              show as <span className="font-semibold">IN</span> for that carrier, so you can chase down any
              missing or extra units. <span className="font-semibold">Read-only</span> — nothing in the database is changed.
            </div>
          </div>
        </div>

        <div className="grid sm:grid-cols-[1fr_220px_auto_auto] gap-3 items-end">
          <div>
            <label className="text-[10px] uppercase tracking-[0.18em] font-bold text-gray-500 block mb-1">
              Carrier stock file
            </label>
            <input
              ref={fileInputRef}
              type="file"
              accept=".csv,.txt,.xlsx,.xlsm"
              onChange={(e) => setFile(e.target.files?.[0] || null)}
              className="block w-full text-sm border border-gray-300 rounded-sm file:mr-3 file:py-2 file:px-3 file:border-0 file:bg-gray-100 file:text-gray-700 file:font-bold file:uppercase file:text-xs file:tracking-[0.12em] file:cursor-pointer hover:file:bg-gray-200"
              data-testid="reconcile-file-input"
            />
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-[0.18em] font-bold text-gray-500 block mb-1">
              Carrier
            </label>
            <select
              value={carrier}
              onChange={(e) => setCarrier(e.target.value)}
              className="w-full border border-gray-300 rounded-sm py-2 px-2 text-sm font-mono bg-white"
              data-testid="reconcile-carrier-select"
            >
              <option value="">— select —</option>
              {carrierOptions.map((c) => (
                <option key={c} value={c}>{c}</option>
              ))}
            </select>
          </div>
          <button
            className="ps-btn ps-btn-primary"
            onClick={runPreview}
            disabled={running || !file || !carrier}
            data-testid="reconcile-run"
          >
            {running ? (<><RefreshCw className="w-4 h-4 animate-spin" /> Comparing…</>) : (<><GitCompare className="w-4 h-4" /> Run comparison</>)}
          </button>
          {(file || result) && (
            <button className="ps-btn ps-btn-secondary" onClick={reset} disabled={running} data-testid="reconcile-reset">Reset</button>
          )}
        </div>

        {file && (
          <div className="text-xs text-gray-500 font-mono">
            {file.name} · {(file.size / 1024).toFixed(1)} KB
          </div>
        )}
      </div>

      {/* Result */}
      {result && (
        <>
          {/* Summary tiles */}
          <div className="grid grid-cols-2 sm:grid-cols-5 gap-3" data-testid="reconcile-summary">
            <SummaryTile label="Carrier reported" value={result.totals.carrier_reported} tone="neutral" />
            <SummaryTile label="In depot (ours)" value={result.totals.depot_in} tone="neutral" />
            <SummaryTile label="Matched" value={result.totals.matched} tone="success" testid="tile-matched" />
            <SummaryTile label="Carrier-only" value={result.totals.at_carrier_not_at_depot} tone="warning" testid="tile-carrier-only" />
            <SummaryTile label="Depot-only" value={result.totals.at_depot_not_at_carrier} tone="error" testid="tile-depot-only" />
          </div>

          <div className="text-xs text-gray-500" data-testid="reconcile-meta">
            <span className="font-semibold">{result.filename}</span> · carrier <span className="font-mono font-bold">{result.carrier_code}</span> · as of {new Date(result.as_of).toLocaleString()}
            {result.totals.iso_mismatches > 0 && (
              <span className="ml-2 text-amber-700 font-semibold">· {result.totals.iso_mismatches} ISO mismatch{result.totals.iso_mismatches > 1 ? "es" : ""}</span>
            )}
          </div>

          {/* Discrepancy lists */}
          <DiscrepancyTable
            title="At carrier, not in our depot"
            hint="The carrier lists these as in our depot, but our gate events disagree. Likely missed gate-in events, mis-keyed numbers, or already gated out."
            rows={result.at_carrier_not_at_depot}
            tone="warning"
            columns={[
              { key: "container_number", label: "Container" },
              { key: "iso_type", label: "Carrier ISO" },
              { key: "condition", label: "Condition" },
            ]}
            exportName={`reconcile_${result.carrier_code}_carrier_only`}
            testid="table-carrier-only"
          />

          <DiscrepancyTable
            title="In our depot, not on carrier report"
            hint="We show these as in depot for this carrier, but they're missing from the report. Could be already gated-out (carrier dropped them) or carrier paperwork lag."
            rows={result.at_depot_not_at_carrier}
            tone="error"
            columns={[
              { key: "container_number", label: "Container" },
              { key: "iso_type", label: "Our ISO" },
              { key: "last_event_at", label: "Last event", format: (v) => v ? String(v).slice(0, 10) : "" },
              { key: "last_location_code", label: "Location" },
            ]}
            exportName={`reconcile_${result.carrier_code}_depot_only`}
            testid="table-depot-only"
          />

          {result.iso_mismatches?.length > 0 && (
            <DiscrepancyTable
              title="ISO type mismatches (info)"
              hint="Same container, different ISO codes on each side. Usually a typo on one of the reports — verify on the EIR."
              rows={result.iso_mismatches}
              tone="warning"
              columns={[
                { key: "container_number", label: "Container" },
                { key: "carrier_iso", label: "Carrier ISO" },
                { key: "depot_iso", label: "Our ISO" },
              ]}
              exportName={`reconcile_${result.carrier_code}_iso_mismatches`}
              testid="table-iso-mismatches"
            />
          )}

          {result.parse_errors?.length > 0 && (
            <details className="border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900" data-testid="reconcile-parse-errors">
              <summary className="cursor-pointer font-semibold">{result.parse_errors.length} parse warning(s)</summary>
              <pre className="font-mono mt-2 bg-white p-2 rounded-sm border border-amber-100 text-[11px]">{JSON.stringify(result.parse_errors, null, 2)}</pre>
            </details>
          )}
        </>
      )}

      {!result && !running && (
        <div className="border border-dashed border-gray-300 bg-gray-50 p-8 text-center text-sm text-gray-500" data-testid="reconcile-empty">
          <GitCompare className="w-8 h-8 mx-auto mb-3 text-gray-400" />
          Pick a carrier and upload their latest stock report to see the diff.
          <div className="text-xs text-gray-400 mt-2">
            Same parsers as the importer — XLSX sheet names, mixed headers, and partial reports are all supported.
          </div>
        </div>
      )}
    </div>
  );
}

function SummaryTile({ label, value, tone, testid }) {
  const tones = {
    neutral: "border-gray-200 bg-white text-gray-900",
    success: "border-emerald-200 bg-emerald-50 text-emerald-900",
    warning: "border-amber-200 bg-amber-50 text-amber-900",
    error: "border-rose-200 bg-rose-50 text-rose-900",
  };
  return (
    <div className={`border p-4 ${tones[tone] || tones.neutral}`} data-testid={testid}>
      <div className="text-[10px] uppercase tracking-[0.18em] font-bold opacity-75">{label}</div>
      <div className="font-mono font-black text-3xl tabular-nums mt-1">{value}</div>
    </div>
  );
}

function DiscrepancyTable({ title, hint, rows, tone, columns, exportName, testid }) {
  const [open, setOpen] = useState(rows.length > 0 && rows.length <= 50);
  const accent = {
    warning: "border-amber-200",
    error: "border-rose-200",
    neutral: "border-gray-200",
  }[tone] || "border-gray-200";
  const headerTone = {
    warning: "bg-amber-50 text-amber-900",
    error: "bg-rose-50 text-rose-900",
    neutral: "bg-gray-50 text-gray-900",
  }[tone] || "bg-gray-50 text-gray-900";

  return (
    <div className={`border ${accent} bg-white`} data-testid={testid}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className={`w-full flex items-center justify-between px-5 py-3 ${headerTone} border-b ${accent}`}
      >
        <div className="flex items-center gap-3 text-left">
          <ChevronRight className={`w-4 h-4 transition-transform ${open ? "rotate-90" : ""}`} />
          <div>
            <div className="text-sm font-bold uppercase tracking-[0.14em]">{title} · <span className="font-mono">{rows.length}</span></div>
            <div className="text-xs opacity-80 normal-case tracking-normal mt-0.5 font-normal">{hint}</div>
          </div>
        </div>
        {rows.length > 0 && (
          <span
            role="button"
            tabIndex={0}
            onClick={(e) => {
              e.stopPropagation();
              downloadCsv(rows, exportName, columns);
            }}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.stopPropagation();
                downloadCsv(rows, exportName, columns);
              }
            }}
            className="ps-btn ps-btn-secondary !py-1 !px-3 text-xs cursor-pointer"
            data-testid={`${testid}-export`}
          >
            <Download className="w-3 h-3" /> CSV
          </span>
        )}
      </button>
      {open && (
        <>
          {rows.length === 0 ? (
            <div className="px-5 py-6 text-sm text-gray-500 flex items-center gap-2">
              <CheckCircle2 className="w-4 h-4 text-emerald-600" /> No discrepancies in this bucket — clean match.
            </div>
          ) : (
            <table className="ps-table">
              <thead>
                <tr>
                  {columns.map((c) => <th key={c.key}>{c.label}</th>)}
                </tr>
              </thead>
              <tbody>
                {rows.slice(0, 500).map((r) => (
                  <tr key={r.container_number}>
                    {columns.map((c) => (
                      <td key={c.key} className={c.key === "container_number" ? "font-mono font-bold" : "font-mono text-sm"}>
                        {c.format ? c.format(r[c.key], r) : (r[c.key] ?? "—")}
                      </td>
                    ))}
                  </tr>
                ))}
                {rows.length > 500 && (
                  <tr><td colSpan={columns.length} className="text-center text-xs text-gray-500 py-3 italic">
                    Showing first 500 of {rows.length}. Export the CSV to see the rest.
                  </td></tr>
                )}
              </tbody>
            </table>
          )}
        </>
      )}
    </div>
  );
}
